import React, {useState, useCallback} from 'react';

import Paper from './Styles/Paper.jsx';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {LightTheme} from './Styles/Styles.js';

import {DEVICE_URL} from '../../imports/ec9xns/ec9xns-connection.js';

import {CONNECTION_STATE} from '../self-service/StateConstants.jsx';

import StartStep from '../self-service/StartStep.jsx';
import ConnectingStep from '../self-service/ConnectingStep.jsx';
import ConnectedStep from '../self-service/ConnectedStep.jsx';
import NotConnectedStep from '../self-service/NotConnectedStep.jsx';
import UpdateAvailableStep from '../self-service/UpdateAvailableStep.jsx';
import UpdateHasCanceledStep from '../self-service/UpdateHasCanceledStep.jsx';
import UpdateExecutingStep from '../self-service/UpdateExecutingStep.jsx';
import UpdateDoneStep from '../self-service/UpdateDoneStep.jsx';
import UpdateDoneRefreshStep from '../self-service/UpdateDoneRefreshStep.jsx';

import RedirectDashboardStep from '../self-service/RedirectDashboardStep.jsx';
import ManualDashboardUpdateStep from '../self-service/ManualDashboardUpdateStep.jsx';
import UnableUpdateStep from '../self-service/UnableUpdateStep.jsx';
import RootCertPage from '../self-service/RootCertPage.jsx';

import PropTypes from 'prop-types';

import '../../main.css';

const MainPage = (props) => {

  MainPage.propTypes = {
    updateURL: PropTypes.string.isRequired,
    rootcert: PropTypes.bool.isRequired
  };

  const theme = createTheme(LightTheme);

  const [connectionState, setConnectionState] = useState(CONNECTION_STATE.START);
  const [updateCandidate, setUpdateCandidate] = useState(null);
  const [scannerInfo, setScannerInfo] = useState({model: null, sn: null, version: null});
  const [deviceURL, setDeviceURL] = useState(DEVICE_URL.HTTPS_USB_RDMSCANNERS_NET);

  /**
   * doUpdate:
   * Delegamos en el motor REAL que ya viene dentro de updateCandidate.
   * - Soporta v2 / legacy / dashboard según lo que haya detectado checkScannerUpdates().
   * - onProgress es opcional (si tu motor lo soporta).
   * - Devuelve un objeto de éxito para que UpdateExecutingStep lo entienda.
   */
  const doUpdate = useCallback(async (onProgress) => {
    if (!updateCandidate || typeof updateCandidate.startUpdate !== 'function') {
      console.error('[MainPage] updateCandidate inválido o sin startUpdate:', updateCandidate);
      throw new Error('Update engine not ready (updateCandidate/startUpdate)');
    }
    await updateCandidate.startUpdate(onProgress);
    return { success: true };
  }, [updateCandidate]);

  /**
   * Refresh en background tras éxito — reintenta conectar/leer versión.
   * Movemos el stepper a RECONNECTING para reutilizar ConnectingStep.
   */
  const onBackgroundRefresh = useCallback(() => {
    setConnectionState(CONNECTION_STATE.RECONNECTING);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <React.StrictMode>
        <div id="mainContent" className="siteSection">
          <div className="widthLimit">
            <Paper>
              {(!props.rootcert) && connectionState == CONNECTION_STATE.START && <StartStep setState={setConnectionState}/>}
              {connectionState == CONNECTION_STATE.CONNECTING &&
                <ConnectingStep setState={setConnectionState} setScannerInfo={setScannerInfo} setDeviceURL={setDeviceURL} reconnecting={false}/>} 
              {connectionState == CONNECTION_STATE.CONNECTED &&
                <ConnectedStep updateURL={props.updateURL} scannerInfo={scannerInfo} setState={setConnectionState} setUpdateCandidate={setUpdateCandidate}/>} 
              {connectionState == CONNECTION_STATE.NOT_CONNECTED && <NotConnectedStep setState={setConnectionState}/>} 
              {connectionState == CONNECTION_STATE.UPDATE_AVAILABLE && <UpdateAvailableStep setState={setConnectionState}/>} 
              {connectionState == CONNECTION_STATE.UPDATE_HAS_CANCELED && <UpdateHasCanceledStep/>} 
              {connectionState == CONNECTION_STATE.UPDATE_EXECUTING &&
                <UpdateExecutingStep
                  setState={setConnectionState}
                  sn={scannerInfo.sn}
                  updateCandidate={updateCandidate}
                  doUpdate={doUpdate}
                  onBackgroundRefresh={onBackgroundRefresh}
                />} 
              {connectionState == CONNECTION_STATE.RECONNECTING &&
                <ConnectingStep setState={setConnectionState} setScannerInfo={setScannerInfo} setDeviceURL={setDeviceURL} reconnecting={true}/>} 
              {connectionState == CONNECTION_STATE.UPDATE_DONE && <UpdateDoneStep/>} 
              {connectionState == CONNECTION_STATE.UPDATE_DONE_REFRESH && <UpdateDoneRefreshStep/>}

              {connectionState == CONNECTION_STATE.REDIRECT_TO_DASHBOARD &&
                <RedirectDashboardStep setState={setConnectionState} deviceURL={deviceURL}/>} 
              {connectionState == CONNECTION_STATE.ASKING_DOWNLOAD_ROOT_CERT && <RootCertPage setState={setConnectionState} updateURL={props.updateURL} isSSWP={true}/>} 
              {connectionState == CONNECTION_STATE.UPDATE_IN_DASHBOARD && <ManualDashboardUpdateStep setState={setConnectionState}/>} 
              {connectionState == CONNECTION_STATE.UNABLE_UPDATE && <UnableUpdateStep/>}

              {props.rootcert && <RootCertPage updateURL={props.updateURL} isSSWP={false}/>} 
            </Paper>
          </div>
        </div>
      </React.StrictMode>
    </ThemeProvider>
  );
};

export default MainPage;
